// "Demo planlayın" form. Posts to exchange_setup.create_demo_request, which
// stores the lead, emails the team (reply-to = the visitor) and sends the
// visitor a confirmation. Opened from every demo CTA on the landing page,
// and by /?demo=1 links (e.g. from the login page).
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { api, endpoints } from '../../../config/api';
import { Input } from '../../../components/ui';
import { Turnstile, TURNSTILE_SITE_KEY } from '../../../components/Turnstile/Turnstile';
import { EASE_OUT } from './primitives';
import { useT } from '../../../i18n';
import './DemoRequestModal.css';

const SIZES = ['1-50', '51-200', '201-1000', '1000+'];

const messages = {
  sizeLabels: {
    tr: { '1-50': '1–50', '51-200': '51–200', '201-1000': '201–1.000', '1000+': '1.000+' },
    en: { '1-50': '1–50', '51-200': '51–200', '201-1000': '201–1,000', '1000+': '1,000+' },
  },
  errName: { tr: 'Lütfen adınızı ve soyadınızı yazın.', en: 'Please enter your full name.' },
  errEmail: { tr: 'Geçerli bir e-posta adresi yazın.', en: 'Enter a valid email address.' },
  errCompany: { tr: 'Lütfen şirket adını yazın.', en: 'Please enter your company name.' },
  errSize: { tr: 'Çalışan sayısını seçin.', en: 'Select your company size.' },
  errConsent: { tr: 'Devam etmek için onay vermeniz gerekiyor.', en: 'Please give your consent to continue.' },
  errThrottled: {
    tr: 'Kısa sürede çok fazla talep gönderildi. Lütfen biraz sonra tekrar deneyin ya da destek@martinci.com adresine yazın.',
    en: 'Too many requests in a short time. Please try again shortly or write to destek@martinci.com.',
  },
  errCaptcha: {
    tr: 'Lütfen insan olduğunuzu doğrulayın.',
    en: 'Please complete the human verification.',
  },
  errSend: {
    tr: 'Talebiniz şu anda gönderilemedi. Lütfen tekrar deneyin ya da destek@martinci.com adresine yazın.',
    en: 'Your request couldn’t be sent right now. Please try again or write to destek@martinci.com.',
  },
  kicker: { tr: '30 dakikalık canlı demo', en: '30-minute live demo' },
  title: { tr: 'Kendi markanızla görün.', en: 'See it with your own brand.' },
  intro: {
    tr: 'Bir iş günü içinde size dönüp uygun bir zaman planlıyoruz. Demoda:',
    en: 'We’ll get back to you within one business day to find a time. In the demo:',
  },
  points: {
    tr: [
      'Outlook’ta yeni ileti açılır açılmaz gelen imza',
      'Grup, dil ve kişiye özel şablon atama',
      'Kampanyalar, yasal uyarılar, memnuniyet anketleri',
      'Microsoft 365 bağlantısı ve birkaç dakikalık kurulum',
    ],
    en: [
      'The signature arriving the moment a new message opens in Outlook',
      'Template assignment by group, language and individual',
      'Campaigns, disclaimers and satisfaction surveys',
      'Connecting Microsoft 365 and a setup that takes minutes',
    ],
  },
  asideFoot: { tr: 'Taahhüt yok · Kredi kartı yok', en: 'No commitment · No credit card' },
  doneTitle: { tr: 'Talebiniz alındı.', en: 'Request received.' },
  doneText: {
    tr: ({ name, email }) => <>Teşekkürler {name}. <strong>{email}</strong> adresine bir onay e-postası gönderdik; bir iş günü içinde size dönüyoruz.</>,
    en: ({ name, email }) => <>Thanks {name}. We’ve sent a confirmation email to <strong>{email}</strong> and will get back to you within one business day.</>,
  },
  backToSite: { tr: 'Siteye dön', en: 'Back to the site' },
  fullName: { tr: 'Ad Soyad', en: 'Full name' },
  workEmail: { tr: 'İş e-postası', en: 'Work email' },
  company: { tr: 'Şirket', en: 'Company' },
  phone: { tr: 'Telefon (isteğe bağlı)', en: 'Phone (optional)' },
  companySize: { tr: 'Çalışan sayısı', en: 'Company size' },
  note: { tr: 'Not (isteğe bağlı)', en: 'Note (optional)' },
  notePlaceholder: {
    tr: 'Örn. kaç alan adı, hangi Outlook sürümleri, özel ihtiyaçlar…',
    en: 'E.g. number of domains, Outlook versions, special requirements…',
  },
  consent: {
    tr: () => <>Bilgilerimin demo talebim için işlenmesine ilişkin{' '}<Link to="/kvkk-aydinlatma-metni" target="_blank">KVKK Aydınlatma Metni</Link>’ni okudum.</>,
    en: () => <>I have read the{' '}<Link to="/kvkk-aydinlatma-metni" target="_blank">privacy notice (KVKK)</Link> on how my details are processed for this demo request.</>,
  },
  sending: { tr: 'Gönderiliyor…', en: 'Sending…' },
  submit: { tr: 'Demo talep et', en: 'Request a demo' },
};

const EMPTY = { full_name: '', email: '', company: '', phone: '', company_size: '', message: '', kvkk_consent: false, website: '' };

function firstError(errs, key) {
  const v = errs && errs[key];
  return Array.isArray(v) ? v[0] : v;
}

export function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const captcha = useRef(null);
  const { t, lang } = useT(messages);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => document.getElementById('demo-name')?.focus(), 350);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open, onClose]);

  // Reset a finished form the next time it opens.
  useEffect(() => {
    if (open && state === 'done') {
      setForm(EMPTY);
      setState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 3) er.full_name = t('errName');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = t('errEmail');
    if (form.company.trim().length < 2) er.company = t('errCompany');
    if (!form.company_size) er.company_size = t('errSize');
    if (!form.kvkk_consent) er.kvkk_consent = t('errConsent');
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setMessage(t('errCaptcha'));
      setState('error');
      return;
    }
    setState('sending');
    setMessage('');
    try {
      await api.post(endpoints.exchange.demoRequest, {
        ...form, email: form.email.trim(), language: lang, turnstile_token: captchaToken,
      });
      setState('done');
    } catch (err) {
      const res = err.response;
      // A token is single-use: get a fresh one for the next attempt.
      captcha.current?.reset();
      if (res?.status === 400 && res.data?.code === 'captcha_failed') {
        setMessage(t('errCaptcha'));
        setState('error');
      } else if (res?.status === 400 && res.data?.details) {
        const d = res.data.details;
        setErrors(Object.fromEntries(Object.keys(d).map((k) => [k, firstError(d, k)])));
        setState('idle');
      } else if (res?.status === 429) {
        setMessage(t('errThrottled'));
        setState('error');
      } else {
        setMessage(t('errSend'));
        setState('error');
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="lp-demo"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-demo-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.25 } }}
        >
          <div className="lp-demo-scrim" onClick={onClose} />
          <motion.div
            className="lp-demo-panel"
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
          >
            <button type="button" className="lp-demo-close" onClick={onClose} aria-label={t('close')}>×</button>

            <aside className="lp-demo-aside">
              <span className="lp-demo-kicker">{t('kicker')}</span>
              <h2 id="lp-demo-title">{t('title')}</h2>
              <p>{t('intro')}</p>
              <ul>
                {t('points').map((p) => <li key={p}>{p}</li>)}
              </ul>
              <span className="lp-demo-aside-foot">{t('asideFoot')}</span>
            </aside>

            <div className="lp-demo-body">
              <AnimatePresence mode="wait">
                {state === 'done' ? (
                  <motion.div
                    key="done"
                    className="lp-demo-done"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE_OUT }}
                  >
                    <svg viewBox="0 0 80 80" className="lp-demo-check" aria-hidden="true">
                      <motion.circle cx="40" cy="40" r="36" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.7, ease: EASE_OUT }} />
                      <motion.path d="M25 41 L35 51 L56 30" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5, delay: 0.55, ease: EASE_OUT }} />
                    </svg>
                    <h3>{t('doneTitle')}</h3>
                    <p>{t('doneText', { name: form.full_name.trim().split(' ')[0], email: form.email.trim() })}</p>
                    <button type="button" className="lp-btn" onClick={onClose}>{t('backToSite')}</button>
                  </motion.div>
                ) : (
                  <motion.form key="form" className="lp-demo-form" onSubmit={submit} noValidate exit={{ opacity: 0 }}>
                    <div className="lp-demo-row">
                      <Input
                        id="demo-name" label={t('fullName')} autoComplete="name" value={form.full_name}
                        onChange={set('full_name')} error={errors.full_name}
                      />
                      <Input
                        id="demo-email" label={t('workEmail')} type="email" autoComplete="email" value={form.email}
                        onChange={set('email')} error={errors.email}
                      />
                    </div>
                    <div className="lp-demo-row">
                      <Input
                        id="demo-company" label={t('company')} autoComplete="organization" value={form.company}
                        onChange={set('company')} error={errors.company}
                      />
                      <Input
                        id="demo-phone" label={t('phone')} type="tel" autoComplete="tel" value={form.phone}
                        onChange={set('phone')} error={errors.phone}
                      />
                    </div>

                    <fieldset className={`lp-demo-sizes ${errors.company_size ? 'has-error' : ''}`}>
                      <legend>{t('companySize')}</legend>
                      <div>
                        {SIZES.map((v) => (
                          <label key={v} className={form.company_size === v ? 'is-on' : ''}>
                            <input type="radio" name="company_size" value={v} checked={form.company_size === v} onChange={set('company_size')} />
                            {t('sizeLabels')[v]}
                          </label>
                        ))}
                      </div>
                      {errors.company_size && <span className="ui-input-error-text">{errors.company_size}</span>}
                    </fieldset>

                    <div className="ui-form-group">
                      <label htmlFor="demo-message">{t('note')}</label>
                      <textarea
                        id="demo-message" rows={3} maxLength={2000} value={form.message} onChange={set('message')}
                        placeholder={t('notePlaceholder')}
                      />
                    </div>

                    {/* Honeypot — hidden from people, filled by bots. */}
                    <input className="lp-demo-hp" tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={set('website')} aria-hidden="true" />

                    <label className={`lp-demo-consent ${errors.kvkk_consent ? 'has-error' : ''}`}>
                      <input type="checkbox" checked={form.kvkk_consent} onChange={set('kvkk_consent')} />
                      <span>{t('consent')}</span>
                    </label>
                    {errors.kvkk_consent && <span className="ui-input-error-text">{errors.kvkk_consent}</span>}

                    <Turnstile ref={captcha} onToken={setCaptchaToken} />

                    {state === 'error' && <p className="lp-demo-error" role="alert">{message}</p>}

                    <button type="submit" className="lp-btn lp-btn-lg lp-demo-submit" disabled={state === 'sending'}>
                      {state === 'sending' ? t('sending') : <>{t('submit')} <span className="lp-btn-arrow">→</span></>}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default DemoRequestModal;
